import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
	CollectionPreferencesProps,
	Container,
	Flashbar,
	Header,
	SpaceBetween,
	Table,
	TextFilter,
	Pagination,
	CollectionPreferences,
	Button,
	Box,
} from '@amzn/awsui-components-console';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { EntitySearchFilter } from 'src/components/entitySearch/entitySearchFilter';
import { EntityAPIResponse } from 'src/@types/SNOWDataServiceTypes';
import { getMatchesCountText, downloadCSV } from 'src/utils/CommonUtils';
import { usePaginatedCallback } from 'src/hooks/usePaginatedCallback';

import { getTableDetails } from './entitySearchUtils';
import { getWorkflowFiltersByName } from 'src/constants/WorkflowDetails';
import { WorkflowInfoPanel } from 'src/components/commonComponents/WorkflowInfoPanel/WorkflowInfoPanel';
import { useFavoritesStore, sortWorkflows } from 'src/stores/favoriteWorkflowsStore/favoritesStore';
import {
	DASHBOARD_COLUMNS,
	TABLE_MESSAGE,
	paginationLabels,
	VISIBLE_COLUMNS,
	PAGE_HEADER_DESCRIPTION,
	PAGE_HEADER,
	COLLECTION_PREFERENCES,
	WORKFLOW_ACTION_URLS,
} from './entitySearchConstants';
import { EntityItems, FilterItem, WorkflowOptionType } from './entitySearch.interface';
import { useWorkflowData } from 'src/components/commonComponents/WorkflowTable/useWorkflowData';
import { useUrlSyncedState } from 'src/hooks/useUrlSyncedState';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

const RESERVED_URL_KEYS = ['workflowType'];

// URL encodes only `=` (PropertyFilter is rendered with hideOperations).
// Encoding new operators in the URL is required if hideOperations is removed.
const parseUrlState = (search: string) => {
	const params = new URLSearchParams(search);
	const filters: FilterItem[] = [];

	params.forEach((value, key) => {
		if (!RESERVED_URL_KEYS.includes(key) && value) {
			filters.push({ key, value });
		}
	});

	return {
		workflowType: params.get('workflowType') || '',
		filters,
	};
};

const buildUrl = (
	pathname: string,
	workflowType: string,
	filters: FilterItem[]
) => {
	const next = new URLSearchParams();

	const cycleIdFilter = filters.find((f) => f.key === 'cycleId' && f.value);

	if (cycleIdFilter) next.set('cycleId', cycleIdFilter.value);

	if (workflowType) next.set('workflowType', workflowType);

	const bulkExecutionIdFilter = filters.find(
		(f) => f.key === 'bulkExecutionId' && f.value
	);

	if (bulkExecutionIdFilter) next.set('bulkExecutionId', bulkExecutionIdFilter.value);

	filters.forEach((f) => {
		if (
			f.key &&
			f.value &&
			f.key !== 'cycleId' &&
			f.key !== 'bulkExecutionId' &&
			!RESERVED_URL_KEYS.includes(f.key)
		) {
			next.set(f.key, f.value);
		}
	});

	const qs = next.toString();

	return qs ? `${pathname}?${qs}` : pathname;
};

interface EntitySearchUrlState {
	workflowType: string;
	filters: FilterItem[];
}

export default function EntitySearch() {
	const { NODES_NOT_FOUND, WORKFLOW_TYPE_NOT_SELECTED, NO_MATCH_FOUND } = TABLE_MESSAGE;
	const location = useLocation();

	const initialUrlState = useMemo(() => parseUrlState(location.search), []);
	const workflowTypeFromUrl = initialUrlState.workflowType;

	const [tableColumns, setTableColumns] = useState(DASHBOARD_COLUMNS);
	const [selectedWorkflowType, setSelectedWorkflowType] = useState(workflowTypeFromUrl || '');
	const [selectedOption, setSelectedOption] = useState<WorkflowOptionType>();
	const [searchButtonDisabled, setSearchButtonDisabled] = useState(true);
	const [clearFiltersButtonDisabled, setClearFiltersButtonDisabled] =
		useState(true);
	const [entitySearched, setEntitySearched] = useState(false);
	const [filterList, setFilterList] = useState<FilterItem[]>(
		initialUrlState.filters.length
			? initialUrlState.filters
			: [{ key: '', value: '' }]
	);
	const [workflowList, setWorkflowList] = useState([
		{
			key: '',
			value: '',
			label: '',
		},
	]);
	const [preferences, setPreferences] =
		useState<CollectionPreferencesProps.Preferences>({
			pageSize: 25,
			contentDisplay: VISIBLE_COLUMNS.map(id => ({ id, visible: true })),
			wrapLines: false,
			stripedRows: getDefaultStripedRows(),
		});
	const [workflowSpecificColumns, setWorkflowSpecificColumns] = useState<Array<{ id: string; label: string }>>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [showEmptyTable, setShowEmptyTable] = useState(true);
	const [isProcessingUrlParam, setIsProcessingUrlParam] = useState(!!workflowTypeFromUrl);
	const [workflowNotFoundError, setWorkflowNotFoundError] = useState('');
	const previousWorkflowTypeRef = useRef<string | null>(null);

	const selectedWorkflowTypeRef = useRef(selectedWorkflowType);

	selectedWorkflowTypeRef.current = selectedWorkflowType;

	const workflowListRef = useRef(workflowList);

	workflowListRef.current = workflowList;

	const filterListRef = useRef<FilterItem[]>(filterList);

	filterListRef.current = filterList;

	const collectionPreferences = useMemo(() => {
		if (!workflowSpecificColumns.length) return COLLECTION_PREFERENCES;

		return {
			...COLLECTION_PREFERENCES,
			contentDisplayPreference: {
				options: [
					...COLLECTION_PREFERENCES.contentDisplayPreference.options,
					...workflowSpecificColumns,
				],
			},
		};
	}, [workflowSpecificColumns]);

	const { favoriteWorkflowIds, toggleFavorite } = useFavoritesStore();

	const sortedWorkflowList = useMemo(
		() => sortWorkflows(workflowList, favoriteWorkflowIds),
		[workflowList, favoriteWorkflowIds]
	);

	const {
		currentPageData: entityItems,
		isLoading,
		flashBarItems,
		currentPageIndex,
		pagesCount,
		hasNextPage,
		nextPage,
		previousPage,
		updatePageSize,
		refresh,
	} = usePaginatedCallback<EntityItems, EntityAPIResponse<object>>({
		pageSize: preferences.pageSize || 25,
		skipInitialFetch: true,
		buildRequest: (pageIndex, continuationToken) => {
			const queryParams: Record<string, unknown> = {
				fetchAllAttributes: true,
				count: preferences.pageSize || 25,
			};

			if (continuationToken) {
				queryParams.continuationToken = continuationToken;
			}

			// If a bulkExecutionId or cycleId is in the filter list, let it
			// drive the search and let the server tell us which workflow it
			// belongs to. Otherwise scope the query to the currently selected
			// workflow.
			const hasBulkExecutionId = filterList.some(
				(f) => f.key === 'bulkExecutionId' && f.value
			);
			const hasCycleId = filterList.some(
				(f) => f.key === 'cycleId' && f.value
			);

			if (selectedWorkflowType && !hasBulkExecutionId && !hasCycleId) {
				queryParams.workflowType = selectedWorkflowType;
			}

			filterList.forEach((filter) => {
				if (filter.value) {
					queryParams[filter.key] = filter.value;
				}
			});

			return {
				path: '/entity',
				method: 'GET',
				queryParams,
			};
		},
		transformResponse: (response: EntityAPIResponse<object>) => {
			// Only cycleId and bulkExecutionId identify a single, specific
			// workflow execution — for those filters, the authoritative
			// workflow is whatever the server returned, so override the
			// current selection to match it. For every other filter
			// (entityName, groupId, region, status, etc.), leave the
			// workflow selection untouched — those can legitimately span
			// multiple workflow types.
			const currentWorkflowType = selectedWorkflowTypeRef.current;
			const currentWorkflowList = workflowListRef.current;
			const currentFilterList = filterListRef.current;
			const firstEntityWorkflowType = response?.entities?.[0]?.WorkflowType;
			const hasBulkExecutionId = currentFilterList.some(
				(f) => f.key === 'bulkExecutionId' && f.value
			);
			const hasCycleId = currentFilterList.some(
				(f) => f.key === 'cycleId' && f.value
			);

			let resolvedWorkflowType = currentWorkflowType;

			const shouldResolveFromResponse =
				(hasBulkExecutionId || hasCycleId) && !!firstEntityWorkflowType;

			if (shouldResolveFromResponse) {
				const match = currentWorkflowList.find(
					(opt) => opt.value === firstEntityWorkflowType
				);

				if (match && match.value !== currentWorkflowType) {
					resolvedWorkflowType = match.value;
					selectedWorkflowTypeRef.current = match.value;
					setSelectedWorkflowType(match.value);
					setSelectedOption(match);
					writeUrl(match.value, currentFilterList);
				}
			}

			const { entityItems, newTableColumns, newVisibleContent } =
				getTableDetails(response, resolvedWorkflowType);

			setTableColumns(newTableColumns);

			if (previousWorkflowTypeRef.current !== resolvedWorkflowType) {
				previousWorkflowTypeRef.current = resolvedWorkflowType;
				setPreferences((prevState) => ({
					...prevState,
					contentDisplay: newVisibleContent.map(id => ({ id, visible: true })),
				}));
			}

			const workflowSpecificCols = newTableColumns
				.filter(col => !DASHBOARD_COLUMNS.find(dc => dc.id === col.id))
				.map(col => ({ id: col.id, label: col.header }));

			setWorkflowSpecificColumns(workflowSpecificCols);

			return {
				items: entityItems,
				continuationToken: response.continuationToken,
			};
		},
	});

	const refreshRef = useRef(refresh);

	refreshRef.current = refresh;

	const triggerRefresh = useCallback(() => {
		setTimeout(() => refreshRef.current(), 0);
	}, []);

	const loadPreferencesData = useCallback(
		(detail) => {
			const previousPageSize = preferences.pageSize;
			const newPageSize = detail.pageSize || 25;
			
			setPreferences(detail);
			
			if (previousPageSize !== newPageSize) {
				updatePageSize(newPageSize);
				triggerRefresh();
			}
		},
		[preferences.pageSize, updatePageSize, triggerRefresh]
	);

	const { writeUrl: writeUrlState } = useUrlSyncedState<EntitySearchUrlState>({
		parse: parseUrlState,
		build: (next) => buildUrl(location.pathname, next.workflowType, next.filters),
		onExternalChange: (next) => {
			setSelectedWorkflowType(next.workflowType);
			if (next.workflowType) {
				const match = workflowListRef.current.find(
					(opt) => opt.value === next.workflowType
				);

				if (match) setSelectedOption(match);
			} else {
				setSelectedOption(undefined);
			}

			setFilterList(next.filters.length ? next.filters : [{ key: '', value: '' }]);
			triggerRefresh();
		},
	});

	const writeUrl = useCallback(
		(workflowType: string, filters: FilterItem[]) => {
			writeUrlState({ workflowType, filters });
		},
		[writeUrlState]
	);

	const changeFilterKeyValuePair = useCallback((filterList: FilterItem[]) => {
		setFilterList(filterList);
		writeUrl(selectedWorkflowType, filterList);

		const hasValidFilters = filterList.some(filter => filter.key && filter.value);

		if (!hasValidFilters && !selectedWorkflowType) {
			setShowEmptyTable(true);
		} else {
			setShowEmptyTable(false);
			triggerRefresh();
		}
	}, [selectedWorkflowType, writeUrl, triggerRefresh]);

	useEffect(() => {
		const hasFilters = filterList.some(
			(item) => item.key !== '' && item.value !== ''
		);

		if (!hasFilters && !selectedWorkflowType) {
			setEntitySearched(false);
			setSearchButtonDisabled(true);
			setShowEmptyTable(true);
		} else {
			setEntitySearched(true);
			setClearFiltersButtonDisabled(false);
			setShowEmptyTable(false);
		}
	}, [filterList, selectedWorkflowType]);

	const {
		isLoading: listWorflowDefinitionLoading,
		allWorkflows,
	} = useWorkflowData();

	useEffect(() => {
		if (allWorkflows.length > 0) {
			const options = allWorkflows.map(
				(option) => ({
					key: option.workflowId,
					value: option.workflowId,
					label: option.workflowDefinitionName,
				})
			);

			setWorkflowList(options);

			if (workflowTypeFromUrl && !selectedOption) {
				const matchingOption = options.find(option => option.value === workflowTypeFromUrl);

				if (matchingOption) {
					setSelectedOption(matchingOption);
					setSelectedWorkflowType(workflowTypeFromUrl);
					setEntitySearched(false);
					setSearchButtonDisabled(false);
					setClearFiltersButtonDisabled(false);
					setWorkflowNotFoundError(''); 
					triggerRefresh();
				} else {
					setWorkflowNotFoundError(`The workflow "${workflowTypeFromUrl}" was not found in available workflow types.`);
				}

				setIsProcessingUrlParam(false);
			} else if (workflowTypeFromUrl) {
				setIsProcessingUrlParam(false);
			} else if (initialUrlState.filters.length > 0) {
				// No workflow in URL, but filters are present → fetch once on load.
				triggerRefresh();
			}
		}
	}, [allWorkflows, workflowTypeFromUrl, selectedOption]);

	const handleOnChangeForSelect = (detail) => {
		setSelectedOption(detail.selectedOption);
		const newWorkflowType = detail.selectedOption.value ? detail.selectedOption.value : '';

		setSelectedWorkflowType(newWorkflowType);

		// bulkExecutionId and cycleId are tied to a specific workflow. Drop both
		// whenever the workflow changes (including being cleared), so stale
		// filters don't travel to the next workflow.
		const nextFilterList = filterList.filter(
			(f) => f.key !== 'bulkExecutionId' && f.key !== 'cycleId'
		);

		if (nextFilterList.length !== filterList.length) {
			setFilterList(nextFilterList.length ? nextFilterList : [{ key: '', value: '' }]);
		}

		writeUrl(newWorkflowType, nextFilterList);

		setEntitySearched(false);
		setSearchButtonDisabled(false);
		setClearFiltersButtonDisabled(false);
		if (detail.selectedOption.value) {
			triggerRefresh();
		}
	};

	const handleSearchButton = () => {
		setSearchButtonDisabled(false);
	};

	const handleClearFilterButton = () => {
		setClearFiltersButtonDisabled(true);
	};

	const handleOnClickSearchButton = () => {
		setEntitySearched(true);
		refresh();
	};

	const handleOnClickClearSelectionButton = () => {
		setSelectedWorkflowType('');
		setSelectedOption(undefined);
		setEntitySearched(false);
		setSearchButtonDisabled(true);
		setFilterList([
			{
				key: '',
				value: '',
			},
		]);
		setClearFiltersButtonDisabled(true);
		setSearchQuery('');
		setShowEmptyTable(true);

		writeUrl('', []);
	};

	const filteredItems = showEmptyTable ? [] : entityItems.filter((item) => {
		if (!searchQuery) return true;

		return Object.values(item).some((value) =>
			String(value).toLowerCase().includes(searchQuery.toLowerCase())
		);
	});

	const loadingFlashbarItems: any[] = [];
	
	if (listWorflowDefinitionLoading) {
		loadingFlashbarItems.push({
			type: 'info',
			loading: true,
			header: 'Loading workflow types',
			content: (
				<SpaceBetween direction="horizontal" size="xs">
					<span>
						{workflowTypeFromUrl 
							? `Loading workflow types and setting up selection for workflow: ${workflowTypeFromUrl}...`
							: 'Loading available workflow types...'
						}
					</span>
				</SpaceBetween>
			),
			dismissible: false,
			id: 'loading-workflows',
		});
	}

	if (workflowNotFoundError) {
		loadingFlashbarItems.push({
			type: 'error',
			header: 'Workflow not found',
			content: workflowNotFoundError,
			dismissible: true,
			onDismiss: () => setWorkflowNotFoundError(''),
			id: 'workflow-not-found',
		});
	}

	const workflowInfoPanelConfig = useMemo(() => {
		if (!selectedWorkflowType) return null;
		const schema = getWorkflowFiltersByName(selectedWorkflowType);
		return schema.infoPanel || null;
	}, [selectedWorkflowType]);

	return (
		<>
			<Flashbar items={[...flashBarItems, ...loadingFlashbarItems]} />
			<Container>
				<SpaceBetween direction="vertical" size="l">
					<Table
						loading={isLoading}
						loadingText="Loading resources"
						columnDefinitions={tableColumns}
						columnDisplay={preferences.contentDisplay}
						items={filteredItems}
						wrapLines={preferences.wrapLines}
						stripedRows={preferences.stripedRows}
						variant="full-page"
						stickyHeader
						header={
							<>
								<Box padding={{ top: 'xxs' }}>
									<Header
										variant="awsui-h1-sticky"
										description={PAGE_HEADER_DESCRIPTION}
										actions={
											<Button
												iconName="download"
												disabled={filteredItems.length === 0 || showEmptyTable}
												onClick={() => downloadCSV(filteredItems, tableColumns, `entity-search-${selectedWorkflowType || 'filtered'}(${filteredItems.length})`)}
											>
												{`Download CSV (${filteredItems.length})`}
											</Button>
										}
									>
										{PAGE_HEADER}
									</Header>
								</Box>
								<Box padding={{ top: 'l', bottom: 's' }}>
									<EntitySearchFilter
										workflowType={selectedWorkflowType}
										changeFilterKeyValuePair={changeFilterKeyValuePair}
										handleOnChangeForSelect={handleOnChangeForSelect}
										workflowListDropdown={sortedWorkflowList}
										selectedOption={selectedOption}
										searchButtonDisabled={searchButtonDisabled}
										setSearchButtonDisabled={handleSearchButton}
										handleOnClickSearchButton={handleOnClickSearchButton}
										handleOnClickClearSelectionButton={
											handleOnClickClearSelectionButton
										}
										loadWorkflowOptionsStatus={
											listWorflowDefinitionLoading ? 'loading' : 'finished'
										}
										clearFiltersButtonDisabled={clearFiltersButtonDisabled}
										setClearFiltersButtonDisabled={handleClearFilterButton}
										favoriteWorkflowIds={favoriteWorkflowIds}
										onToggleFavorite={toggleFavorite}
										initialFilterTokens={initialUrlState.filters}
										filterTokens={filterList}
									/>
								</Box>
							</>
						}
						empty={
							<TableWithNoData
								message={
									entityItems.length > 0 && filteredItems.length === 0
										? NO_MATCH_FOUND
										: entitySearched 
											? NODES_NOT_FOUND 
											: WORKFLOW_TYPE_NOT_SELECTED
								}
								actionButton={
									WORKFLOW_ACTION_URLS[selectedWorkflowType] && 
									entitySearched && 
									entityItems.length === 0 && 
									filteredItems.length === 0 ? (
											<Button
												variant="primary"
												href={WORKFLOW_ACTION_URLS[selectedWorkflowType]}
											>
											+ Create New Execution for {selectedOption?.label || selectedWorkflowType}
											</Button>
										) : undefined
								}
							/>
						}
						pagination={
							<Pagination
								currentPageIndex={currentPageIndex}
								pagesCount={pagesCount}
								openEnd={hasNextPage}
								onNextPageClick={nextPage}
								onPreviousPageClick={previousPage}
								ariaLabels={paginationLabels}
							/>
						}
						preferences={
							<CollectionPreferences
								onConfirm={({ detail }) => loadPreferencesData(detail)}
								{...collectionPreferences}
								preferences={preferences}
							/>
						}
						resizableColumns
						filter={
							<SpaceBetween direction="vertical" size="s">
								<TextFilter
									filteringText={searchQuery}
									onChange={({ detail }) => setSearchQuery(detail.filteringText)}
									filteringAriaLabel="Search on this page"
									countText={getMatchesCountText(filteredItems.length)}
									filteringPlaceholder="Search on this page"
								/>
								{workflowInfoPanelConfig && (
									<WorkflowInfoPanel config={workflowInfoPanelConfig} />
								)}
							</SpaceBetween>
						}
					/>
				</SpaceBetween>
			</Container>
		</>
	);
}
